// The native engine (docs/design/native-app.md, decisions 1 and 14): one binary frame per call
// to a host that runs the Rust runtime, its events read from the host, and the files a panel
// would give or take through the host too. Two hosts carry the frames: the loopback test host
// over HTTP on 127.0.0.1 (engine/loopback.ts), which writes the page's token and device into a
// meta tag, and the installed app over Tauri's IPC (engine/app.ts), which sets a global from
// its initialization script. This file knows neither; it packs and unpacks the frames.

import { EngineFailure, type Engine, type EngineEvent, type Request, type ResponseOf } from './protocol.ts'
import { attach, decodeFrame, detach, encodeFrame } from './frame.ts'

/// What the installed app's initialization script sets, frozen.
export interface Installed {
  device: string
  version: string
  build: string
  role: string
}

/// Which host the page is under, and what it wrote into the page.
export type NativeContext = { kind: 'loopback'; base: string; token: string; device: string; signIn: 'link' | 'device' } | ({ kind: 'app' } & Installed)

declare global {
  interface Window {
    __INTERPLAE_DESKTOP__?: Installed
  }
}

/// The host the page is under, or null in a plain browser: the loopback's meta tag, or the
/// app's frozen global. Installed mode forces the native engine whatever the query says; the
/// loopback signs the page in by the mailed link unless the runner asks for the device flow.
export function detectHost(): NativeContext | null {
  const app = window.__INTERPLAE_DESKTOP__
  if (app) return { kind: 'app', ...app }
  const meta = document.querySelector('meta[name="interplae-host"]')
  const token = meta?.getAttribute('content')
  if (!token) return null
  const signIn = new URLSearchParams(location.search).has('device-signin') ? 'device' : 'link'
  return { kind: 'loopback', base: location.origin, token, device: meta?.getAttribute('data-device') ?? '', signIn }
}

/// What carries a frame to the runtime and the runtime's events back.
export interface NativeTransport {
  /// Settled once the host can take a call; an event sent before is never missed.
  ready: Promise<void>
  call(frame: Uint8Array): Promise<ArrayBuffer>
  listen(listener: (event: EngineEvent) => void): void
}

export const frameOf = (json: unknown, attachments: [string, ArrayBuffer][]): Uint8Array => encodeFrame({ json, attachments })

/// The files a pick frame carries: its JSON names them in order and the attachments hold them.
export function filesOf(bytes: ArrayBuffer): File[] | null {
  const { json, attachments } = decodeFrame(bytes)
  if (json === null) return null
  return attachments.map(([name, content]) => new File([content], name))
}

/// The page's own server call as the frame the runtime's `fetch_frame` takes: the method, the
/// path, the headers and the timeout in the JSON, the body beside it. The origin is the host's.
/// `named` adds to the frame what the app reads beside the request: the aligner's window names the
/// organisation a call goes to, since no organisation binds it (brief 49).
export function fetchFrame(input: RequestInfo | URL, init: RequestInit = {}, named: { origin?: string } = {}): Uint8Array {
  const url = new URL(typeof input === 'string' ? input : input instanceof URL ? input.href : input.url, location.origin)
  const headers = [...new Headers(init.headers ?? {}).entries()]
  const body = init.body
  const bytes = body instanceof ArrayBuffer ? body : body instanceof Uint8Array ? body.buffer.slice(body.byteOffset, body.byteOffset + body.byteLength) : typeof body === 'string' ? new TextEncoder().encode(body).buffer : null
  const timeoutMs = bytes && bytes.byteLength > 1 << 20 ? 600_000 : 30_000
  return frameOf({ method: init.method ?? 'GET', path: `${url.pathname}${url.search}`, headers, timeoutMs, ...named }, bytes ? [['body', bytes as ArrayBuffer]] : [])
}

/// The runtime's answer as the Response the page's API reads; a server that did not answer
/// throws the TypeError a browser's fetch throws.
export function responseOf(bytes: ArrayBuffer): Response {
  const { json, attachments } = decodeFrame(bytes)
  const answer = json as { status: number; headers: [string, string][] } | { failure: string }
  if ('failure' in answer) throw new TypeError(answer.failure)
  const body = attachments.find(([name]) => name === 'body')?.[1] ?? null
  return new Response(answer.status === 204 || answer.status === 304 ? null : body, { status: answer.status, headers: answer.headers })
}

export function nativeEngine(transport: NativeTransport): Engine {
  return {
    listen(listener) {
      transport.listen(listener)
    },
    async call<R extends Request>(request: R): Promise<ResponseOf<R>> {
      await transport.ready
      const { json, attachments } = detach(request)
      let answer: ArrayBuffer
      try {
        answer = await transport.call(frameOf(json, attachments))
      } catch (e) {
        throw new EngineFailure({ kind: 'other', message: e instanceof Error ? e.message : String(e) })
      }
      const frame = decodeFrame(answer)
      const envelope = frame.json as { ok: true; result: unknown } | { ok: false; error: EngineFailure['error'] }
      if (!envelope.ok) throw new EngineFailure(envelope.error)
      return attach(envelope.result, frame.attachments) as ResponseOf<R>
    },
  }
}
